import { ChangeDetectorRef, Component, DestroyRef, inject, model, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { LoaderModule } from '@michelin/theme';
import { YearScrollerComponent } from '../fleet/year-scroller/year-scroller.component';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, startWith, switchMap, tap } from 'rxjs';
import { ForecastService } from '../../shared/services/forecast.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContextService } from '../../shared/services/context.service';
import { AutocompleteComponent } from '../../shared/components/autocomplete/autocomplete.component';
import { FormControl } from '@angular/forms';
import { ModificationButtonsComponent } from '../../shared/components/modification-buttons/modification-buttons.component';
import { ForecastTableComponent } from './forecast-table/forecast-table.component';
import { MatDialog } from '@angular/material/dialog';
import { ForecastDetailsModalComponent } from './forecast-details-modal/forecast-details-modal.component';
import { NavigationConfirmService } from '../../shared/services/navigation-confirm.service';
import {NoDataComponent} from "../../shared/components/no-data/no-data.component";

@Component({
  selector: 'app-forecast',
  imports: [
    TranslatePipe,
    LoaderModule,
    YearScrollerComponent,
    AutocompleteComponent,
    ModificationButtonsComponent,
    ForecastTableComponent,
    NoDataComponent
  ],
  templateUrl: './forecast.component.html',
  styleUrl: './forecast.component.scss'
})
export class ForecastComponent implements OnInit {
  private dialog = inject(MatDialog);
  /**
   * Sizes
   */
  protected sizes: string[];
  protected isSizesLoading: boolean = false;
  protected sizesFilterCtrl = new FormControl<string>('');
  protected filteredSizes$!: Observable<string[]>;
  protected sizeLoadingError: string | null;

  /**
   * Search parameters
   */
  protected siteId: number;
  protected year: number = new Date().getFullYear();
  protected selectedSize: string;

  @ViewChild(ForecastTableComponent)
  protected forecastTable: ForecastTableComponent;

  protected isLoadingData: boolean = false;
  protected isSavingData: boolean = false;
  protected isModificationPending: boolean = false;

  constructor(
    private readonly forecastService: ForecastService,
    private readonly contextService: ContextService,
    private readonly destroyRef: DestroyRef,
    private readonly cdr: ChangeDetectorRef,
    private readonly navConfirmService: NavigationConfirmService
  ) {}

  ngOnInit(): void {
    this.contextService.siteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
        this.loadSizes();
      }
    });
  }

  protected loadSizes() {
    this.isSizesLoading = true;
    this.forecastService
      .getAllSizeFull(1, this.siteId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.sizes = result;
        if (this.sizes && this.sizes.length > 0) {
          //Usage of setTimeout to ensure synchronization with the view and Check the selected value
          setTimeout(() => {
            this.sizesFilterCtrl.setValue(this.sizes[0]);
            this.selectedSize = this.sizes[0];
          });
        }
        this.isSizesLoading = false;
        this.setupAutoComplete();
        this.cdr.detectChanges();
      });
  }

  private setupAutoComplete() {
    this.filteredSizes$ = this.sizesFilterCtrl.valueChanges.pipe(
      startWith(''),
      tap(() => {
        this.isSizesLoading = true;
      }),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((size: string) => {
        const query = (size ?? '').toString().toLowerCase()
        return of(this.sizes.filter((data) => data.toLowerCase().includes(query)));
      }),
      map((items) => {
        this.isSizesLoading = false;
        this.sizeLoadingError = null;
        return items || ([] as string[]);
      }),
      catchError(() => {
        this.isSizesLoading = false;
        this.sizeLoadingError = 'Failed to fetch results. Please try again.';
        return of([] as string[]);
      })
    ) as Observable<string[]>;
  }

  protected saveModifications() {
    this.forecastTable.saveReplPlan();
  }

  protected resetModifications() {
    this.forecastTable.resetModifications();
  }

  protected onElementSelected(element: string) {
    if (this.isModificationPending) {
      this.navConfirmService.confirmNavigation(
        () => {
          this.selectedSize = element;
        },
        () => {
          this.sizesFilterCtrl.setValue(this.selectedSize, { emitEvent: false });
        }
      );
    } else {
      this.selectedSize = element;
    }
  }

  protected openForecastDetailsHelpModal() {
    this.dialog.open(ForecastDetailsModalComponent);
  }

  protected copyAllFitment() {
    this.forecastTable.copyAllFitmentsAndRecalculate();
  }

  protected copyAllProposedReplenishment() {
    this.forecastTable.copyAllProposedReplAndRecalculate();
  }
}
